import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import usePersistedState from '../../hooks/usePersistedState';

const RANGE_OPTIONS = ['24h', '7d', '30d'];
const REQUEST_LIST_LIMIT = 100;

const bucketKeyFor = (dt, range) => (range === '24h' ? dt.toFormat('yyyy-MM-dd HH') : dt.toFormat('yyyy-MM-dd'));
const bucketLabelFor = (dt, range) => (range === '24h' ? dt.toFormat('HH:00') : dt.toFormat('dd.MM'));

function buildBuckets(range) {
   const now = DateTime.now();
   const steps = range === '24h' ? 23 : range === '7d' ? 6 : 29;
   const unit = range === '24h' ? 'hours' : 'days';
   const buckets = [];
   for (let i = steps; i >= 0; i--) {
      const dt = now.minus({ [unit]: i }).startOf(range === '24h' ? 'hour' : 'day');
      buckets.push({ key: bucketKeyFor(dt, range), label: bucketLabelFor(dt, range), success: 0, error: 0, totalDurationMs: 0, durationSamples: 0 });
   }
   return buckets;
}

const formatDate = (val) => {
   const dt = DateTime.fromISO(val);
   return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm:ss') : '';
}

// Client-side bucketing over one fetch of recent raw rows, same pattern this
// app already uses for its other hand-built charts (no charting library) -
// throttled AI calls (60s apart) keep daily volume low enough that a single
// limit=3000 fetch comfortably covers a month of history. `provider` makes
// this reusable across every AI provider in the system (originally
// Gemini-only, generalized when OpenAI was added) - mounted once per
// provider by whichever page renders it. `contextPrefix` is the other axis:
// when set (and `provider` left unset), the chart aggregates ACROSS every
// provider but only for call-sites whose context starts with that prefix
// (e.g. "AiWordService." for vocabulary-only usage on /vocabulary-config) -
// provider stops mattering entirely for that view, per explicit request.
// `chartKey` backs the persisted-state keys since `provider` alone can't
// anymore (it may be undefined in contextPrefix mode).
export default function ApiUsageChart({ provider, contextPrefix, chartKey }) {
   const langRoot = language[language.lang];
   const t = langRoot.apiUsageChart;
   const stateKey = chartKey || provider;

   const [rows, setRows] = useState([]);
   const [status, setStatus] = useState('loading');
   const [range, setRange] = usePersistedState('api-usage-range-' + stateKey, '7d');
   const [hoveredCount, setHoveredCount] = useState(null);
   const [hoveredDuration, setHoveredDuration] = useState(null);
   const [showTable, setShowTable] = usePersistedState('api-usage-showTable-' + stateKey, false);
   const [showRequests, setShowRequests] = usePersistedState('api-usage-showRequests-' + stateKey, false);
   const [expandedId, setExpandedId] = useState(null);
   const [detailCache, setDetailCache] = useState({});
   const [detailLoading, setDetailLoading] = useState(false);

   useEffect(() => {
      setStatus('loading');
      axios.get('/api/api-call-logs', { params: { provider, contextPrefix, limit: 3000 }, withCredentials: true })
         .then(response => {
            setRows(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch(() => setStatus('done'));
   }, [provider, contextPrefix]);

   const { buckets, stats, contextBreakdown } = useMemo(() => {
      const from = range === '24h' ? DateTime.now().minus({ hours: 24 }) : range === '7d' ? DateTime.now().minus({ days: 7 }) : DateTime.now().minus({ days: 30 });
      const bucketList = buildBuckets(range);
      const bucketMap = new Map(bucketList.map(b => [b.key, b]));

      let successCount = 0, errorCount = 0, totalDuration = 0, durationSamples = 0;
      const contextCounts = new Map();

      rows.forEach(row => {
         const dt = DateTime.fromISO(row.calledAt);
         if (!dt.isValid || dt < from) return;
         const bucket = bucketMap.get(bucketKeyFor(dt, range));
         const isSuccess = row.status === 'SUCCESS';
         if (bucket) {
            if (isSuccess) bucket.success += 1; else bucket.error += 1;
            if (row.durationMs != null) { bucket.totalDurationMs += row.durationMs; bucket.durationSamples += 1; }
         }
         if (isSuccess) successCount += 1; else errorCount += 1;
         if (row.durationMs != null) { totalDuration += row.durationMs; durationSamples += 1; }
         const ctxKey = row.context || '?';
         contextCounts.set(ctxKey, (contextCounts.get(ctxKey) || 0) + 1);
      });

      const total = successCount + errorCount;
      return {
         buckets: bucketList,
         stats: {
            total,
            errorCount,
            successRate: total ? Math.round((successCount / total) * 100) : null,
            avgDuration: durationSamples ? Math.round(totalDuration / durationSamples) : null,
         },
         contextBreakdown: [...contextCounts.entries()].sort((a, b) => b[1] - a[1]),
      };
   }, [rows, range]);

   const recentRequests = useMemo(
      () => [...rows].sort((a, b) => new Date(b.calledAt) - new Date(a.calledAt)).slice(0, REQUEST_LIST_LIMIT),
      [rows]
   );

   const toggleRow = (id) => {
      if (expandedId === id) {
         setExpandedId(null);
         return;
      }
      setExpandedId(id);
      if (!detailCache[id]) {
         setDetailLoading(true);
         axios.get('/api/api-call-logs/' + id, { withCredentials: true })
            .then(response => setDetailCache(prev => ({ ...prev, [id]: response.data })))
            .catch(() => {})
            .finally(() => setDetailLoading(false));
      }
   };

   const maxCount = Math.max(1, ...buckets.map(b => b.success + b.error));
   const maxDuration = Math.max(1, ...buckets.map(b => (b.durationSamples ? b.totalDurationMs / b.durationSamples : 0)));
   // thin out x-axis labels once there are more buckets than comfortably fit
   // side by side (see anti-patterns: never a number/label on every point)
   const labelStep = Math.max(1, Math.ceil(buckets.length / 8));

   return (
      <div className="vocab-config-tab-body">
         <div className="api-usage-header">
            <div className="api-usage-range-toggle">
               {RANGE_OPTIONS.map(r => (
                  <span key={r} className={r === range ? 'active' : ''} onClick={() => setRange(r)}>{t['range' + r]}</span>
               ))}
            </div>
         </div>

         {status === 'loading' ? <p className="api-usage-empty">{langRoot['Loading ...']}</p> : ''}

         {status === 'done' && rows.length === 0 ? <p className="api-usage-empty">{t.noUsageData}</p> : ''}

         {status === 'done' && rows.length > 0 ? (
            <>
               <div className="api-usage-stats">
                  <div className="api-usage-stat-tile">
                     <span className="api-usage-stat-value">{stats.total}</span>
                     <span className="api-usage-stat-label">{t.statTotal}</span>
                  </div>
                  <div className="api-usage-stat-tile">
                     <span className="api-usage-stat-value">{stats.successRate != null ? stats.successRate + '%' : '—'}</span>
                     <span className="api-usage-stat-label">{t.statSuccessRate}</span>
                  </div>
                  <div className="api-usage-stat-tile">
                     <span className="api-usage-stat-value api-usage-stat-error">{stats.errorCount}</span>
                     <span className="api-usage-stat-label">{t.statErrors}</span>
                  </div>
                  <div className="api-usage-stat-tile">
                     <span className="api-usage-stat-value">{stats.avgDuration != null ? stats.avgDuration + ' ms' : '—'}</span>
                     <span className="api-usage-stat-label">{t.statAvgDuration}</span>
                  </div>
               </div>

               <div className="api-usage-charts-row">
                  <div className="api-usage-chart-block">
                     <div className="api-usage-chart-title">{t.chartRequests}</div>
                     <div className="api-usage-legend">
                        <span className="api-usage-legend-item"><i className="api-usage-swatch api-usage-swatch-success"></i>{t.legendSuccess}</span>
                        <span className="api-usage-legend-item"><i className="api-usage-swatch api-usage-swatch-error"></i>{t.legendError}</span>
                     </div>
                     <div className="api-usage-bars-wrap">
                        <div className="api-usage-bars">
                           {buckets.map((b, i) => {
                              const totalB = b.success + b.error;
                              return (
                                 <div key={b.key} className="api-usage-bar-col" tabIndex={0}
                                    onMouseEnter={() => setHoveredCount(i)} onMouseLeave={() => setHoveredCount(prev => (prev === i ? null : prev))}
                                    onFocus={() => setHoveredCount(i)} onBlur={() => setHoveredCount(prev => (prev === i ? null : prev))}>
                                    {hoveredCount === i ? (
                                       <div className="api-usage-tooltip">
                                          {b.label}<br />{t.legendSuccess}: <b>{b.success}</b><br />{t.legendError}: <b>{b.error}</b>
                                       </div>
                                    ) : ''}
                                    <div className="api-usage-bar-stack" style={{ height: ((totalB / maxCount) * 100) + '%' }}>
                                       {b.error > 0 ? <div className="api-usage-bar-seg api-usage-seg-error" style={{ height: ((b.error / totalB) * 100) + '%' }}></div> : ''}
                                       {b.success > 0 ? <div className="api-usage-bar-seg api-usage-seg-success" style={{ height: ((b.success / totalB) * 100) + '%' }}></div> : ''}
                                    </div>
                                 </div>
                              );
                           })}
                        </div>
                        <div className="api-usage-bar-labels">
                           {buckets.map((b, i) => <span key={b.key}>{i % labelStep === 0 ? b.label : ''}</span>)}
                        </div>
                     </div>
                  </div>

                  <div className="api-usage-chart-block">
                     <div className="api-usage-chart-title" style={{marginBottom:25}} >{t.chartDuration}</div>
                     <div className="api-usage-bars-wrap">
                        <div className="api-usage-bars">
                           {buckets.map((b, i) => {
                              const avg = b.durationSamples ? b.totalDurationMs / b.durationSamples : 0;
                              return (
                                 <div key={b.key} className="api-usage-bar-col" tabIndex={0}
                                    onMouseEnter={() => setHoveredDuration(i)} onMouseLeave={() => setHoveredDuration(prev => (prev === i ? null : prev))}
                                    onFocus={() => setHoveredDuration(i)} onBlur={() => setHoveredDuration(prev => (prev === i ? null : prev))}>
                                    {hoveredDuration === i ? (
                                       <div className="api-usage-tooltip">{b.label}<br />{t.chartDuration}: <b>{Math.round(avg)} ms</b></div>
                                    ) : ''}
                                    <div className="api-usage-bar-single" style={{ height: ((avg / maxDuration) * 100) + '%' }}></div>
                                 </div>
                              );
                           })}
                        </div>
                        <div className="api-usage-bar-labels">
                           {buckets.map((b, i) => <span key={b.key}>{i % labelStep === 0 ? b.label : ''}</span>)}
                        </div>
                     </div>
                  </div>
               </div>

               {contextBreakdown.length > 0 ? (
                  <div className="api-usage-context-row">
                     {contextBreakdown.map(([ctx, count]) => (
                        <span className="api-usage-context-chip" key={ctx}><b>{count}</b> {ctx}</span>
                     ))}
                  </div>
               ) : ''}

               <div className="api-usage-table-toggle" onClick={() => setShowTable(v => !v)}>
                  {showTable ? t.hideTable : t.showTable}
               </div>
               {showTable ? (
                  <div className="student-lessons-table-wrap">
                     <table className="student-lessons-table">
                        <thead>
                           <tr>
                              <th></th>
                              <th>{t.legendSuccess}</th>
                              <th>{t.legendError}</th>
                              <th>{t.chartDuration}</th>
                           </tr>
                        </thead>
                        <tbody>
                           {buckets.map(b => (
                              <tr key={b.key}>
                                 <td>{b.label}</td>
                                 <td>{b.success}</td>
                                 <td>{b.error}</td>
                                 <td>{b.durationSamples ? Math.round(b.totalDurationMs / b.durationSamples) + ' ms' : ''}</td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               ) : ''}

               <div className="api-usage-table-toggle" onClick={() => setShowRequests(v => !v)}>
                  {showRequests ? t.hideRequests : t.showRequests}
               </div>
               {showRequests ? (
                  <div className="student-lessons-table-wrap">
                     <table className="student-lessons-table">
                        <thead>
                           <tr>
                              <th>{t.colTime}</th>
                              <th>{t.colContext}</th>
                              <th>{t.colStatus}</th>
                              <th>{t.colHttpCode}</th>
                              <th>{t.colDuration}</th>
                           </tr>
                        </thead>
                        <tbody>
                           {recentRequests.map(row => (
                              <React.Fragment key={row.id}>
                                 <tr className="api-usage-request-row" onClick={() => toggleRow(row.id)}>
                                    <td>{formatDate(row.calledAt)}</td>
                                    <td>{row.context}</td>
                                    <td>
                                       <span className={'api-usage-status-badge ' + (row.status === 'SUCCESS' ? 'ok' : 'error')}>
                                          {row.status === 'SUCCESS' ? t.legendSuccess : t.legendError}
                                       </span>
                                    </td>
                                    <td>{row.httpStatusCode != null ? row.httpStatusCode : '—'}</td>
                                    <td>{row.durationMs != null ? row.durationMs + ' ms' : '—'}</td>
                                 </tr>
                                 {expandedId === row.id ? (
                                    <tr className="api-usage-detail-row">
                                       <td colSpan={5}>
                                          {detailLoading && !detailCache[row.id] ? (
                                             <p className="api-usage-empty">{langRoot['Loading ...']}</p>
                                          ) : detailCache[row.id] ? (
                                             <div className="api-usage-detail">
                                                {detailCache[row.id].errorMessage ? (
                                                   <p className="api-usage-detail-error">{detailCache[row.id].errorMessage}</p>
                                                ) : ''}
                                                <span className="api-usage-detail-label">{t.requestBodyLabel}</span>
                                                <pre className="cron-config-details">{detailCache[row.id].requestBody || '—'}</pre>
                                                <span className="api-usage-detail-label">{t.responseBodyLabel}</span>
                                                <pre className="cron-config-details">{detailCache[row.id].responseBody || '—'}</pre>
                                             </div>
                                          ) : ''}
                                       </td>
                                    </tr>
                                 ) : ''}
                              </React.Fragment>
                           ))}
                        </tbody>
                     </table>
                  </div>
               ) : ''}
            </>
         ) : ''}
      </div>
   );
}
